import React from "react";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang, LANGUAGES } from "../contexts/LanguageContext";
import { Menu, LogOut, User, Shield, Globe, ChevronDown } from "lucide-react";
import DeitiesMenu, { useDeityGroups } from "./DeitiesMenu";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const _oldLinks = [
  { to: "/names", label: "1008 Names" },
  { to: "/daily-verse", label: "Daily Verse" },
  { to: "/pilgrimage", label: "Cambodia Yatra" },
  { to: "/teachers", label: "Teachers" },
  { to: "/donate", label: "Seva" },
  { to: "/seva-club", label: "Blessing Club" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const navLinks = [
    { to: "/names", label: t("nav.names") },
    { to: "/festivals", label: "Festivals" },
    { to: "/teachers", label: "Teachers" },
    { to: "/shop", label: "Shop" },
    { to: "/donate", label: t("nav.donate") },
  ];
  const moreLinks = [
    { to: "/pilgrimage", label: t("nav.pilgrimage") },
    { to: "/daily-verse", label: t("nav.verse") },
    { to: "/bhajans", label: "Bhajans & Aartis" },
    { to: "/jaagran", label: "Shivratri Jaagran" },
    { to: "/satsangs", label: "Live Satsangs" },
    { to: "/seva-club", label: t("nav.club") },
    { to: "/transparency", label: "Transparency" },
    { to: "/ngo", label: "NGO Partners" },
    { to: "/scholarships", label: "Scholarships" },
    { to: "/about", label: t("nav.about") },
  ];
  const deityGroups = useDeityGroups();
  const dashPath = { guru: "/teacher/dashboard", supplier: "/supplier/dashboard", ngo: "/ngo/dashboard" }[user?.role] || "/dashboard";

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-0 z-40 backdrop-blur-md bg-[#FAF6F0]/85 border-b border-[#D4AF37]/35"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
            <span className="w-10 h-10 rounded-full bg-[#4A0E17] text-[#D4AF37] flex items-center justify-center font-devanagari text-2xl shadow-md border border-[#D4AF37]/50">
              ॐ
            </span>
            <div className="leading-tight">
              <div className="font-serif text-xl text-[#4A0E17] tracking-tight whitespace-nowrap">Pancham Dham</div>
              <div className="overline text-[0.6rem] whitespace-nowrap">Cambodia · 1008 Names</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <DeitiesMenu />
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm font-medium whitespace-nowrap transition-sacred ${isActive ? "text-[#E65100]" : "text-[#1F1412] hover:text-[#4A0E17]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-more-menu" className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-[#1F1412] hover:text-[#4A0E17]">More <ChevronDown className="w-3.5 h-3.5" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#FFFDF9] border-[#D4AF37]/40">
                {moreLinks.map((l) => (
                  <DropdownMenuItem key={l.to} data-testid={`nav-more-${l.to.slice(1)}`} onClick={() => navigate(l.to)}>{l.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="lang-switcher" className="hidden md:inline-flex items-center gap-1.5 h-10 px-3 rounded-full border border-[#D4AF37]/40 bg-[#FFFDF9] hover:bg-[#F3E5AB]/50 text-sm text-[#4A0E17] font-medium transition-sacred">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase tracking-wider font-mono text-xs">{lang}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#FFFDF9] border-[#D4AF37]/40">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={lang === l.code ? "bg-[#F3E5AB]/40" : ""}
                  >
                    <span className="flex-1">{l.native}</span>
                    <span className="text-xs text-[#8C6D3B] uppercase font-mono ml-3">{l.code}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="hidden xl:inline-flex">
              <Link to="/pilgrimage" data-testid="book-pilgrimage-cta" className="btn-saffron text-sm whitespace-nowrap">
                {t("nav.book")}
              </Link>
            </span>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="w-10 h-10 rounded-full border border-[#D4AF37]/50 flex items-center justify-center bg-[#FFFDF9] hover:bg-[#F3E5AB]/50 transition-sacred">
                    <User className="w-5 h-5 text-[#4A0E17]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#FFFDF9] border-[#D4AF37]/40">
                  <div className="px-2 py-1.5 text-sm font-semibold text-[#4A0E17]">{user.name}</div>
                  <div className="px-2 pb-2 text-xs text-[#63534E]">{user.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(dashPath)}>
                    My Dashboard
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <span className="hidden md:inline-flex"><Link data-testid="nav-login" to="/login" className="btn-ghost-gold text-sm whitespace-nowrap">Sign in</Link></span>
              </>
            )}
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden p-2">
              <Menu className="w-6 h-6 text-[#4A0E17]" />
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden py-4 border-t border-[#D4AF37]/30 flex flex-col gap-3 max-h-[75vh] overflow-y-auto">
            <details data-testid="mobile-deities-menu" className="group">
              <summary className="text-base font-medium text-[#1F1412] cursor-pointer list-none flex items-center gap-1">Gods & Goddesses <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" /></summary>
              <div className="pl-3 mt-2 space-y-3">
                <Link to="/deities" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#E65100]">See all →</Link>
                {deityGroups.map(([cat, list]) => (
                  <div key={cat}>
                    <div className="overline !text-[0.6rem]">{cat}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      {list.map((d) => <Link key={d.slug} to={`/deities/${d.slug}`} data-testid={`mobile-deity-${d.slug}`} onClick={() => setOpen(false)} className="text-sm text-[#1F1412]">{d.name}</Link>)}
                    </div>
                  </div>
                ))}
              </div>
            </details>
            {[...navLinks, ...moreLinks].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.slice(1)}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-[#E65100]" : "text-[#1F1412]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {!user && (
              <Link data-testid="mobile-login" to="/login" className="btn-ghost-gold w-fit text-sm">Sign in</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
